import React, { useState } from 'react';
import { X, Mail, Check, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Small modal that captures name + email, POSTs the current estimator
 * state to /api/estimator-quote/send, and confirms the send.
 *
 * Reused by the three estimators (self-publishing, audiobook, podcast).
 * The parent passes `getPayload()` — a fresh snapshot of the current
 * selections — so the dialog never holds stale data if the user tweaks
 * the estimator after opening it.
 */
export default function EmailQuoteDialog({ open, onClose, getPayload, testId = 'email-quote' }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const reset = () => {
    setName('');
    setEmail('');
    setBusy(false);
    setError('');
    setSent(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim() || !email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }
    setBusy(true);
    try {
      const body = { email: email.trim(), name: name.trim() || null, ...getPayload() };
      const res = await fetch(`${API}/api/estimator-quote/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      setSent(true);
    } catch (err) {
      setError('Something went wrong. Please try again or email jo@audioandco.com directly.');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#1E2A44]/70 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      data-testid={`${testId}-dialog`}
      onClick={close}
    >
      <div
        className="bg-white rounded-[16px] shadow-[0_25px_60px_rgba(0,0,0,0.35)] w-full max-w-md p-6 sm:p-7 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={close}
          className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-[#94A3B8] hover:text-[#1E2A44] hover:bg-[#F1F5F9] transition-colors"
          aria-label="Close"
          data-testid={`${testId}-close`}
        >
          <X className="w-4 h-4" />
        </button>

        {sent ? (
          <div className="text-center py-4">
            <div className="w-14 h-14 rounded-full bg-[#FFF3F6] mx-auto flex items-center justify-center">
              <Check className="w-7 h-7 text-[#BC516F]" strokeWidth={3} />
            </div>
            <h3 className="font-display font-bold text-[#1E2A44] text-xl mt-4">On its way.</h3>
            <p className="text-[#475569] text-sm mt-2 leading-relaxed">
              Check <b>{email}</b> in a minute or two — your PDF quote is attached.
              If it doesn&rsquo;t land, check spam or reply to this email and Jo will send it manually.
            </p>
            <button
              type="button"
              onClick={close}
              className="mt-6 inline-flex items-center gap-2 bg-[#BC516F] hover:bg-[#A7468A] text-white font-semibold px-5 py-2.5 rounded-[10px] transition-colors text-sm"
              data-testid={`${testId}-done`}
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full bg-[#FFF3F6] flex items-center justify-center">
                <Mail className="w-4 h-4 text-[#BC516F]" />
              </div>
              <h3 className="font-display font-bold text-[#1E2A44] text-lg">Email me this quote</h3>
            </div>
            <p className="text-[#475569] text-sm mt-2 leading-relaxed">
              We&rsquo;ll send you a one-page PDF summary of your selections and the indicative ballpark.
              Nothing else, no drip campaign — just the quote in your inbox to share with anyone helping you plan.
            </p>
            <form onSubmit={submit} className="mt-5 space-y-3">
              <div>
                <label htmlFor={`${testId}-name`} className="text-[#1E2A44] font-semibold text-xs uppercase tracking-widest">Your name (optional)</label>
                <input
                  id={`${testId}-name`}
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full mt-1 rounded-[10px] border border-[#E2E8F0] focus:border-[#BC516F] focus:outline-none px-3 py-2.5 text-sm text-[#1E2A44]"
                  data-testid={`${testId}-name-input`}
                />
              </div>
              <div>
                <label htmlFor={`${testId}-email`} className="text-[#1E2A44] font-semibold text-xs uppercase tracking-widest">Email <span className="text-[#BC516F]">*</span></label>
                <input
                  id={`${testId}-email`}
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full mt-1 rounded-[10px] border border-[#E2E8F0] focus:border-[#BC516F] focus:outline-none px-3 py-2.5 text-sm text-[#1E2A44]"
                  data-testid={`${testId}-email-input`}
                />
              </div>
              {error && (
                <p className="text-sm text-[#BC516F]" data-testid={`${testId}-error`}>{error}</p>
              )}
              <button
                type="submit"
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#BC516F] hover:bg-[#A7468A] disabled:bg-[#94A3B8] text-white font-semibold px-5 py-3 rounded-[10px] transition-colors text-sm"
                data-testid={`${testId}-submit`}
              >
                {busy ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4" /> Send me the quote
                  </>
                )}
              </button>
              <p className="text-[#94A3B8] text-xs text-center">
                We&rsquo;ll use your email only to send the quote and, if you consent later, occasional Audio &amp; Co updates.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
